import * as z from 'zod';

/**
 * Zod schemas for the support-inbox `conversation` domain, shaped like the real
 * `main-service` chat contract (`/api/v1/chats…`, snake_case). Zod is the single
 * source of truth (CLAUDE.md): every type below is INFERRED, and the api layer
 * parses each response with these schemas so contract drift fails at the
 * boundary.
 *
 * Wire tolerance: the backend serializes Go `Null*` wrappers, so most fields are
 * `nullish` and objects are `.loose()` — a missing field must render its
 * fallback, never crash the inbox.
 */

/** Tolerant id — the wire sends ints; route/search params may carry strings. */
export const conversationIdSchema = z.union([z.string(), z.number()]);
export type ConversationId = z.infer<typeof conversationIdSchema>;

const numLike = z.union([z.string(), z.number()]);

/**
 * The six inbox folders (the secondary nav). Each folder is a preset of the
 * chat-list filter — see `buildConversationListFilter`.
 */
export const conversationFolderSchema = z.enum([
  'my',
  'clients',
  'couriers',
  'restaurants',
  'open',
  'resolved',
]);
export type ConversationFolder = z.infer<typeof conversationFolderSchema>;

/**
 * Status tabs above the list: Новые=1, В прогрессе=2, Все=ALL,
 * Решено=3, Не решено=4. Serialized as strings in the URL.
 */
export const chatStatusTabSchema = z.enum(['ALL', '1', '2', '3', '4']);
export type ChatStatusTab = z.infer<typeof chatStatusTabSchema>;

/**
 * The `status` URL param. The router JSON-parses search values, so `?status=2`
 * arrives as the NUMBER 2 — accept numbers too, and fall back to "Все" on junk
 * or a missing value instead of crashing the route on a stale bookmark.
 */
export const chatStatusParamSchema = z
  .union([chatStatusTabSchema, z.number()])
  .transform(String)
  .pipe(chatStatusTabSchema)
  .catch('ALL');

/**
 * Route search params of the inbox (the URL is the source of truth):
 * folder, open chat, status tab, the urgency sort and the header search.
 */
export const conversationSearchSchema = z.object({
  folder: conversationFolderSchema.default('my'),
  chat: conversationIdSchema.optional(),
  status: chatStatusParamSchema.default('ALL'),
  /** cpanel `isAsc` → `order_by_urgency` (earliest unanswered first). */
  urgent: z.boolean().optional(),
  /** Header search → the `username` list filter. */
  search: z.coerce.string().optional(),
});
export type ConversationSearch = z.infer<typeof conversationSearchSchema>;

/** A chat participant (the user, or the responsible operator). */
export const chatUserSchema = z
  .object({
    id: numLike.nullish(),
    role: z.string().nullish(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
  })
  .loose();
export type ChatUser = z.infer<typeof chatUserSchema>;

/** Last message preview carried on a list row. */
export const chatLastMessageSchema = z
  .object({
    message: z.string().nullish(),
    sent_at: z.string().nullish(),
    outgoing: z.boolean().nullish(),
    seen: z.boolean().nullish(),
  })
  .loose();
export type ChatLastMessage = z.infer<typeof chatLastMessageSchema>;

const supportTitleSchema = z.object({ title: z.string().nullish() }).loose();

/** Help-center tags of a support chat (category + topic path). */
export const chatSupportSchema = z
  .object({
    category: z
      .object({ id: numLike.nullish(), title: z.string().nullish() })
      .loose()
      .nullish(),
    topic_path: z.array(supportTitleSchema).nullish(),
    topics: z.array(supportTitleSchema).nullish(),
  })
  .loose();
export type ChatSupport = z.infer<typeof chatSupportSchema>;

/** A chat — list row and detail share one shape (`ChatWithLastMessage`). */
export const chatSchema = z
  .object({
    id: conversationIdSchema,
    subject: z.string().nullish(),
    user: chatUserSchema.nullish(),
    order_id: numLike.nullish(),
    display_id: numLike.nullish(),
    status: z.number().nullish(),
    created_at: z.string().nullish(),
    updated_at: z.string().nullish(),
    responsible: chatUserSchema.nullish(),
    new_user_id: numLike.nullish(),
    confirmed: z.boolean().nullish(),
    last_message: chatLastMessageSchema.nullish(),
    last_courier_message_sent_at: z.string().nullish(),
    support: chatSupportSchema.nullish(),
    /** Destination of the linked order, when the backend embeds it. */
    order: z.object({ to: z.string().nullish() }).loose().nullish(),
  })
  .loose();
export type Chat = z.infer<typeof chatSchema>;

/** Per-status totals behind the tab badges (`count_details`). */
export const chatCountDetailsSchema = z
  .object({
    open_count: z.number().nullish(),
    acquired_count: z.number().nullish(),
    closed_resolved_count: z.number().nullish(),
    closed_not_resolved_count: z.number().nullish(),
  })
  .loose();
export type ChatCountDetails = z.infer<typeof chatCountDetailsSchema>;

/** `GET chats` envelope (`MyChatsDto`). */
export const chatListSchema = z
  .object({
    data: z
      .array(chatSchema)
      .nullish()
      .transform((rows) => rows ?? []),
    count: z
      .number()
      .nullish()
      .transform((count) => count ?? 0),
    count_details: chatCountDetailsSchema.nullish(),
    page_size: z.number().nullish(),
    current_page: z.number().nullish(),
    last_page: z.number().nullish(),
  })
  .loose();
export type ChatList = z.infer<typeof chatListSchema>;

/** One message in a thread (`ChatMessage`). */
export const chatMessageSchema = z
  .object({
    id: conversationIdSchema,
    message: z
      .string()
      .nullish()
      .transform((text) => text ?? ''),
    outgoing: z
      .boolean()
      .nullish()
      .transform((out) => out === true),
    seen: z
      .boolean()
      .nullish()
      .transform((seen) => seen === true),
    created_at: z.string(),
    files: z
      .array(z.string())
      .nullish()
      .transform((files) => files ?? []),
    chat_id: numLike.nullish(),
  })
  .loose();
export type ChatMessage = z.infer<typeof chatMessageSchema>;

/** `GET chats/{id}/messages` envelope (`ChatMessagesDto`). */
export const chatMessagesSchema = z
  .object({
    data: z
      .array(chatMessageSchema)
      .nullish()
      .transform((rows) => rows ?? []),
    count: z
      .number()
      .nullish()
      .transform((count) => count ?? 0),
    page_size: z.number().nullish(),
    current_page: z.number().nullish(),
    last_page: z.number().nullish(),
  })
  .loose();
export type ChatMessages = z.infer<typeof chatMessagesSchema>;

/** An operator-only note pinned to a chat thread. */
export const chatNoteSchema = z
  .object({
    id: conversationIdSchema,
    text: z
      .string()
      .nullish()
      .transform((text) => text ?? ''),
    user_full_name: z.string().nullish(),
    created_at: z.string(),
  })
  .loose();
export type ChatNote = z.infer<typeof chatNoteSchema>;

export const chatNotesSchema = z.array(chatNoteSchema);

/** Macro languages (the RU/UZ toggle of the macros panel). */
export const macroLangSchema = z.enum(['ru', 'uz']);
export type MacroLang = z.infer<typeof macroLangSchema>;

/** A reusable reply template. */
export const chatMacroSchema = z
  .object({
    id: conversationIdSchema,
    title: z.string().nullish(),
    message: z
      .string()
      .nullish()
      .transform((text) => text ?? ''),
    lang: z.string().nullish(),
  })
  .loose();
export type ChatMacro = z.infer<typeof chatMacroSchema>;

export const chatMacrosSchema = z.array(chatMacroSchema);

/** Presence of a chat participant (`GET /ws/users/{id}/check-online`). */
export const chatPresenceSchema = z.object({ is_online: z.boolean().nullish() }).loose();
export type ChatPresence = z.infer<typeof chatPresenceSchema>;

/**
 * The courier snapshot shown in the chat side panel (`GET admin/couriers/{id}`).
 * Only the fields the panel reads — everything else is passed through.
 */
export const chatCourierSchema = z
  .object({
    id: numLike.nullish(),
    phone: z.string().nullish(),
    transport_type: z.string().nullish(),
    balance: numLike.nullish(),
    average_rating: numLike.nullish(),
    activity: numLike.nullish(),
    order_count: numLike.nullish(),
    today_orders_count: numLike.nullish(),
    courier_cash: z
      .object({
        is_cash: z.boolean().nullish(),
        cash_balance: numLike.nullish(),
      })
      .loose()
      .nullish(),
  })
  .loose();
export type ChatCourier = z.infer<typeof chatCourierSchema>;

/** A call-center operator a chat can be handed over to (`GET admin/users`). */
export const chatOperatorSchema = z
  .object({
    id: numLike,
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    role: z.string().nullish(),
  })
  .loose();
export type ChatOperator = z.infer<typeof chatOperatorSchema>;

export const chatOperatorListSchema = z
  .object({
    data: z
      .array(chatOperatorSchema)
      .nullish()
      .transform((rows) => rows ?? []),
    count: z
      .number()
      .nullish()
      .transform((count) => count ?? 0),
  })
  .loose();
export type ChatOperatorList = z.infer<typeof chatOperatorListSchema>;
